/**
 * Plan policy v2: the project lifecycle rules both dashboards decide with.
 *
 * Byte-identical in www.bunnyquery.com and skapi-rebrand, and free of imports on
 * purpose: tests/plan-state.test.mjs loads this file straight into `node --test`
 * (Node 22 type stripping). So it may hold only erasable TypeScript (no enums,
 * no namespaces, no parameter properties) and nothing that needs Vue, the `@/`
 * alias or the DOM. It decides; the words live in plan-copy.ts.
 *
 * service-spec.ts imports these names and exports them again, so older imports
 * from "@/code/service-spec" keep resolving.
 */

/* ── lifecycle constants ──────────────────────────────────────────────────── */

/**
 * A suspended project (active -1) can be deleted this long after it was
 * suspended. Fixed milliseconds, not calendar months: keep in sync with
 * SERVICE_DELETE_AFTER_DAYS in infra/layer/response_interface/python/service_state.py,
 * which the DELETE_SERVICE sweep in infra/admin/admin/remove_resource/index.py
 * reads. The sweep also waits for a final notice and runs every 12 hours, so the
 * date built from this is the EARLIEST deletion, and copy says "as early as".
 */
export const SERVICE_DELETE_AFTER_DAYS = 30;
export const SERVICE_DELETE_AFTER_MS = SERVICE_DELETE_AFTER_DAYS * 24 * 60 * 60 * 1000;

/**
 * Strictly below this `active` value a project is CONTAINED: banned, or held
 * for law enforcement. Mirrors CONTAINED_ACTIVE_CEILING in the backend layer
 * (service_state.py). The backend hides a contained project from everyone but
 * super admins and never sweeps it.
 */
export const CONTAINED_ACTIVE_CEILING = -100;

/** service row `suspend_reason` written by the quota evaluator. A -1 row without
 *  it is a legacy or manual suspension. */
export const SUSPEND_REASON_QUOTA = "quota";

/**
 * Email storage suspends only where the plan's limit is 0, and there only above
 * this many bytes. A plan that includes email storage deletes its oldest
 * newsletters instead of suspending, and since owner decision 11 of 2026-09-17
 * that is every plan: Free carries 50 MiB of its own. The branch below stays for
 * the regions still serving the older Free limit of 0.
 * Mirrors QUOTA_SUSPEND_MIN_BYTES in service_state.py.
 */
export const QUOTA_SUSPEND_MIN_BYTES = 10 * 1024 * 1024;

/** A stored level at or above this share of its limit counts as "close". */
export const NEAR_LIMIT_RATIO = 0.9;

/**
 * `service.active` is the project's lifecycle state and the ONLY reliable
 * signal for "can this project still be used". 1 running, 0 disabled,
 * -1 suspended, -2 deleted, below -100 contained.
 */
export const SERVICE_STATUS_LABEL: Record<string, string> = {
    "0": "Disabled",
    "1": "Running",
    "-1": "Suspended",
    "-2": "Deleted",
};

export const isServiceContained = (active?: number): boolean =>
    typeof active === "number" && active < CONTAINED_ACTIVE_CEILING;

/** Label for an `active` code; never undefined, so callers can .toLowerCase() it. */
export const serviceStatusLabel = (active?: number): string =>
    isServiceContained(active)
        ? "Contained"
        : SERVICE_STATUS_LABEL[String(active)] || "Unknown";

/** Running is the only state in which the project serves requests and its
 *  settings may be edited. */
export const isServiceRunning = (active?: number): boolean => active === 1;

export const isServiceSuspended = (active?: number): boolean => active === -1;

/**
 * Whether the owner may list, refresh and DELETE data (records, files, hosting
 * files, newsletters). A suspended owner must be able to delete data to get
 * back under the limits, so -1 is included. Creating and uploading stay locked
 * outside 1 and 0; deleted and contained projects allow nothing.
 */
export const canManageData = (active?: number): boolean =>
    active === 1 || active === 0 || active === -1;

/** A service row's `subs_id` ("<subscription>#<item>", or a list of them) names a
 *  Stripe subscription. An empty string means none. */
export function hasSubscriptionId(subsId: unknown): boolean {
    if (typeof subsId === "string") return subsId.length > 0;
    if (Array.isArray(subsId)) return subsId.some((s) => typeof s === "string" && s.length > 0);
    return false;
}

/* ── stored limits ────────────────────────────────────────────────────────── */

/** The stored levels that can suspend a hard-stop project. fpool is the pooled
 *  file storage: cloud files + hosting + the BunnyQuery index. */
export type StoredLimitKey = "dbst" | "fpool" | "emlst";

export const STORED_LIMIT_KEYS: StoredLimitKey[] = ["dbst", "fpool", "emlst"];

export const STORED_LIMIT_LABEL: Record<StoredLimitKey, string> = {
    dbst: "Database storage",
    fpool: "File storage",
    emlst: "Email storage",
};

/** Bytes per key; null means the plan sets no ceiling for it. */
export type StoredLimits = Record<StoredLimitKey, number | null>;

export type StoredUsage = Record<StoredLimitKey, number>;

/** The `storage_info` shape pull-services joins onto a service row. */
export type StorageInfoLike = {
    database?: number;
    cloud?: number;
    host?: number;
    file?: number;
    email?: number;
} | null | undefined;

const byteCount = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Stored usage per limit key. A missing or unreadable field counts as 0. */
export function storedUsage(info?: StorageInfoLike): StoredUsage {
    return {
        dbst: byteCount(info?.database),
        fpool: byteCount(info?.cloud) + byteCount(info?.host) + byteCount(info?.file),
        emlst: byteCount(info?.email),
    };
}

/** True when every key is either null (no ceiling) or a finite byte count. Limits
 *  are unknown until GET /plan answers, and an unknown limit must never read as
 *  "nothing exceeded". */
export function limitsKnown(limits: StoredLimits | null | undefined): limits is StoredLimits {
    if (!limits || typeof limits !== "object") return false;
    return STORED_LIMIT_KEYS.every((k) => {
        const v = limits[k];
        return v === null || (typeof v === "number" && Number.isFinite(v) && v >= 0);
    });
}

/**
 * Keys whose usage is strictly over its limit (the check stream_storage uses, so
 * a limit of 0 with 1 byte stored is exceeded). null when the limits are unknown.
 */
export function exceededStoredLimits(info: StorageInfoLike, limits: StoredLimits | null | undefined): StoredLimitKey[] | null {
    if (!limitsKnown(limits)) return null;
    const usage = storedUsage(info);
    return STORED_LIMIT_KEYS.filter((k) => limits[k] !== null && usage[k] > (limits[k] as number));
}

/**
 * Keys at or above `ratio` of a non-zero limit without being over it. null when
 * the limits are unknown.
 */
export function nearStoredLimits(info: StorageInfoLike, limits: StoredLimits | null | undefined, ratio: number = NEAR_LIMIT_RATIO): StoredLimitKey[] | null {
    if (!limitsKnown(limits)) return null;
    const usage = storedUsage(info);
    return STORED_LIMIT_KEYS.filter((k) => {
        const limit = limits[k];
        return limit !== null && limit > 0 && usage[k] >= limit * ratio && usage[k] <= limit;
    });
}

/**
 * The ceilings a plan SUSPENDS at, from the ceilings it stops writes at.
 *
 * - A plan without a hard stop (Premium, Unlimited) never suspends: all null.
 * - Database and file storage suspend at their limit.
 * - Email storage suspends ONLY where the plan's limit is 0, and there only
 *   above QUOTA_SUSPEND_MIN_BYTES. Where the plan includes some (Free and
 *   Standard, owner decision 11), the oldest newsletters are deleted instead, so
 *   it is not a suspension limit and the meter is drawn against the real limit.
 *
 * Comparing usage to these strictly is the backend's plan-change rule (cancel,
 * downgrade) and its resume rule. null when the limits are unknown.
 */
export function suspensionLimits(limits: StoredLimits | null | undefined, hardStop: boolean = true): StoredLimits | null {
    if (!limitsKnown(limits)) return null;
    if (hardStop === false) return { dbst: null, fpool: null, emlst: null };
    return {
        dbst: limits.dbst,
        fpool: limits.fpool,
        emlst: limits.emlst === 0 ? QUOTA_SUSPEND_MIN_BYTES : null,
    };
}

/** Keys that would keep (or put) a project on this plan suspended. null when the
 *  limits are unknown. */
export function suspendingLimits(info: StorageInfoLike, limits: StoredLimits | null | undefined, hardStop: boolean = true): StoredLimitKey[] | null {
    return exceededStoredLimits(info, suspensionLimits(limits, hardStop));
}

/**
 * The service row's `suspend_over`: a comma list drawn from dbst, fpool and emlst
 * (a String, so set and list normalization cannot reorder it). Unknown keys are
 * dropped and the result is in STORED_LIMIT_KEYS order. null when the attribute
 * is absent, not a string, or names no known key.
 */
export function suspendOverFromRow(row?: { suspend_over?: unknown } | null): StoredLimitKey[] | null {
    const raw = row?.suspend_over;
    if (typeof raw !== "string") return null;
    const named = new Set(raw.split(",").map((s) => s.trim()));
    const keys = STORED_LIMIT_KEYS.filter((k) => named.has(k));
    return keys.length ? keys : null;
}

/* ── deletion clock ───────────────────────────────────────────────────────── */

export type SuspendedRowLike = {
    active?: number;
    suspended_at?: unknown;
    subs_id?: unknown;
    suspend_reason?: unknown;
    suspend_over?: unknown;
} | null | undefined;

/** The earliest deletion instant of a suspended row: suspended_at + 30 days.
 *  null unless active is -1 and suspended_at is a positive number. */
export function deletionAt(row: SuspendedRowLike): number | null {
    if (!row || row.active !== -1) return null;
    const at = row.suspended_at;
    if (typeof at !== "number" || !Number.isFinite(at) || at <= 0) return null;
    return at + SERVICE_DELETE_AFTER_MS;
}

/** A suspended project with a Stripe subscription is never deleted while the
 *  subscription stays (owner decision, round 2 answer 4). */
export function deletionHeld(row: SuspendedRowLike): boolean {
    return !!row && row.active === -1 && hasSubscriptionId(row.subs_id);
}

export type SuspensionClockKind = "held" | "scheduled" | "due" | "undated";
export type SuspensionClock = { kind: SuspensionClockKind; at: number | null };

/**
 * What a suspended row's deletion looks like right now. null when the row is not
 * suspended.
 * - held: a subscription is attached, so it is not deleted.
 * - scheduled: deletion can happen as early as `at`, which is in the future.
 * - due: `at` has passed; the next sweep may delete it.
 * - undated: no suspended_at on the row.
 */
export function suspensionClock(row: SuspendedRowLike, now: number): SuspensionClock | null {
    if (!row || row.active !== -1) return null;
    if (deletionHeld(row)) return { kind: "held", at: null };
    const at = deletionAt(row);
    if (at === null) return { kind: "undated", at: null };
    return { kind: at > now ? "scheduled" : "due", at };
}

/* ── project state ────────────────────────────────────────────────────────── */

export type SubscriptionLike = {
    status?: string | null;
    cancel_at?: number | null;
    current_period_end?: number | null;
} | null | undefined;

export type ProjectState =
    | "contained"
    | "deleted"
    | "suspended"
    | "disabled"
    | "past_due"
    | "cancel_scheduled"
    | "over_limit_running"
    | "running"
    | "unknown";

/** A cancellation is scheduled while Stripe still carries cancel_at on a
 *  subscription that has not ended. */
export function cancelScheduled(sub: SubscriptionLike): boolean {
    return !!sub && !!sub.cancel_at && sub.status !== "canceled";
}

/**
 * One state per project, in this order: unknown, contained, deleted, suspended,
 * disabled; then for a running project past_due, cancel_scheduled, and
 * over_limit_running when `overLimit` (it holds more than its plan suspends at).
 */
export function projectState(active: unknown, sub?: SubscriptionLike, overLimit: boolean = false): ProjectState {
    if (typeof active !== "number" || !Number.isFinite(active)) return "unknown";
    if (active < CONTAINED_ACTIVE_CEILING) return "contained";
    if (active === -2) return "deleted";
    if (active === -1) return "suspended";
    if (active === 0) return "disabled";
    if (active !== 1) return "unknown";
    if (sub?.status === "past_due") return "past_due";
    if (cancelScheduled(sub)) return "cancel_scheduled";
    return overLimit ? "over_limit_running" : "running";
}

export type ResumeKind = "subscribe" | "upgrade" | "support";

/**
 * How a suspended project gets its plan back, matching the button the
 * subscription page will show.
 * - A plan the owner cannot change (perpetual licences, Unlimited): support.
 * - No subscription, or a plan without a hard stop: subscribe.
 * - A hard-stop plan with a live subscription: upgrade.
 */
export function resumeKind(hardStop: boolean, hasSubscription: boolean, canChangePlan: boolean): ResumeKind {
    if (!canChangePlan) return "support";
    if (!hasSubscription || hardStop === false) return "subscribe";
    return "upgrade";
}

/** Whether the subscription page may offer "Switch to Free": only a running
 *  project with a Stripe subscription whose plan the owner can change. */
export function canSwitchToFree(params: { hasSubscription: boolean; subsId: unknown; canChangePlan: boolean; active: unknown }): boolean {
    return !!params.hasSubscription
        && hasSubscriptionId(params.subsId)
        && !!params.canChangePlan
        && params.active === 1;
}

/** Midnight UTC of the first day of the month after `nowMs`, in ms. Monthly
 *  counters (bandwidth) reset then. */
export function firstOfNextUtcMonth(nowMs: number): number {
    const d = new Date(nowMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

/** "YYYYMM" of `nowMs` in UTC, the month key of the row's `egrblk`. */
export function utcMonthKey(nowMs: number): string {
    const d = new Date(nowMs);
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Whether requests are being refused for bandwidth this month: the served
 * enforce_egress_hard_stop switch is on, the plan has a hard stop, and the row's
 * `egrblk` names the current UTC month (the edge blocks only in that month).
 */
export function bandwidthBlocked(egrblk: unknown, enforce: boolean, hardStop: boolean, nowMs: number): boolean {
    return enforce === true && hardStop !== false && typeof egrblk === "string" && egrblk === utcMonthKey(nowMs);
}

/* ── pure selectors behind the copy ───────────────────────────────────────── */

export type BannerId =
    | "contained"
    | "deleted"
    | "unknown"
    | "suspended"
    | "disabled"
    | "past_due"
    | "cancel_scheduled"
    | "over_limit_running"
    | "near_limit"
    | "bandwidth";

/**
 * Which banners a project page shows, in order. A stopped project shows its
 * state banner alone. A running one shows past_due or cancel_scheduled first,
 * then either the over-limit or the near-limit warning, then bandwidth.
 */
export function bannerIds(state: ProjectState, flags: { over?: boolean; near?: boolean; bandwidth?: boolean } = {}): BannerId[] {
    if (state === "contained" || state === "deleted" || state === "unknown"
        || state === "suspended" || state === "disabled") {
        return [state];
    }
    const ids: BannerId[] = [];
    if (state === "past_due" || state === "cancel_scheduled") ids.push(state);
    if (flags.over || state === "over_limit_running") ids.push("over_limit_running");
    else if (flags.near) ids.push("near_limit");
    if (flags.bandwidth) ids.push("bandwidth");
    return ids;
}

export type SuspendedKind = "over" | "back_under" | "checking" | "generic";

/**
 * What the suspended banner can truthfully say about WHY.
 * - over: the row's suspend_over, or else the client's own comparison, names keys.
 * - back_under: a quota suspension whose row lists nothing over (suspend_over
 *   written as an empty string) AND whose usage, against known limits, is under
 *   every suspension limit. Only then may copy say it returns to normal.
 * - checking: a quota suspension the client cannot place yet (limits unknown, or
 *   no suspend_over on the row). Copy must not claim it is back under.
 * - generic: a legacy or manual suspension with nothing known to be over.
 */
export function suspendedKind(row: SuspendedRowLike, clientKeys: StoredLimitKey[] | null): { kind: SuspendedKind; keys: StoredLimitKey[] } {
    const rowKeys = suspendOverFromRow(row);
    if (rowKeys) return { kind: "over", keys: rowKeys };
    if (clientKeys && clientKeys.length) return { kind: "over", keys: clientKeys };
    if (row?.suspend_reason === SUSPEND_REASON_QUOTA) {
        // Only an explicitly EMPTY suspend_over says nothing is over. A String that
        // names only keys this build does not know is unknown, not "none".
        const rowSaysNone = typeof row.suspend_over === "string"
            && row.suspend_over.split(",").every((s) => s.trim() === "");
        if (rowSaysNone && clientKeys !== null) return { kind: "back_under", keys: [] };
        return { kind: "checking", keys: [] };
    }
    return { kind: "generic", keys: [] };
}

export type StateCellKind =
    | "contained"
    | "deleted"
    | "unknown"
    | "disabled"
    | "running"
    | "over_limit"
    | "payment_failed"
    | "deletes"
    | "deleting"
    | "suspended"
    | "suspends"
    | "free_from"
    | "cancels";

/**
 * The project list's state cell, before wording.
 * - suspended: `deletes` with the date while it is in the future, `deleting` once
 *   it has passed, plain `suspended` when held or undated.
 * - cancel_scheduled: `suspends` when the project holds more than Free suspends
 *   at, `free_from` when it fits, `cancels` when that is not known yet.
 */
export function stateCell(state: ProjectState, clock: SuspensionClock | null, cancelAtMs: number | null, freeKeys: StoredLimitKey[] | null): { kind: StateCellKind; at: number | null } {
    switch (state) {
        case "contained":
        case "deleted":
        case "unknown":
        case "disabled":
        case "running":
            return { kind: state, at: null };
        case "over_limit_running":
            return { kind: "over_limit", at: null };
        case "past_due":
            return { kind: "payment_failed", at: null };
        case "suspended":
            if (clock?.kind === "scheduled") return { kind: "deletes", at: clock.at };
            if (clock?.kind === "due") return { kind: "deleting", at: clock.at };
            return { kind: "suspended", at: null };
        case "cancel_scheduled":
            if (freeKeys === null) return { kind: "cancels", at: cancelAtMs };
            return { kind: freeKeys.length ? "suspends" : "free_from", at: cancelAtMs };
    }
    return { kind: "unknown", at: null };
}

export type CancelConsequence = "already_suspended" | "checking" | "suspends" | "fits";

/**
 * What cancelling does to this project, before wording.
 * - already_suspended: cancelling changes neither the suspension nor its clock.
 * - checking: the Free limits are not known yet; the dialog must not offer the
 *   cancel until they are.
 * - suspends / fits: whether it holds more than Free suspends at.
 */
export function cancelConsequence(active: unknown, freeKeys: StoredLimitKey[] | null): CancelConsequence {
    if (active === -1) return "already_suspended";
    if (freeKeys === null) return "checking";
    return freeKeys.length ? "suspends" : "fits";
}

/* ── newsletter sending ───────────────────────────────────────────────────── */

/**
 * The newsletter abuse controls of owner decision 12 (2026-09-17).
 *
 * These are POLICY numbers, not plan limits, and that is why they are authored
 * here while every ceiling on a plan card is read from GET /plan: the payload
 * carries none of them. A project's own monthly send ceiling (`emlsd`) is a plan
 * limit and is never written down in this file.
 *
 * Keep in step with the send gate in infra mail/filter_mail.
 */

/** Complaints in the current UTC month at which a project stops sending
 *  newsletters, on EVERY plan. */
export const COMPLAINT_SHUTOFF_COUNT = 5;

/** The complaint rate that stops sending once at least
 *  COMPLAINT_SHUTOFF_MIN_SENDS mails have gone out this month, whichever comes
 *  first. Held as the percent the copy prints, so no rounding can turn 0.5 into
 *  "0.49999999999999994" on a meter. */
export const COMPLAINT_SHUTOFF_PERCENT = 0.5;
export const COMPLAINT_SHUTOFF_RATE = COMPLAINT_SHUTOFF_PERCENT / 100;
export const COMPLAINT_SHUTOFF_MIN_SENDS = 100;

/** Newsletter mails ONE OWNER may send in a UTC month across every free-plan
 *  project they have, on top of each project's own monthly ceiling. Standard and
 *  Premium are always counted per project and have no owner cap. */
export const FREE_OWNER_MONTHLY_SENDS = 200;

/**
 * The one service row attribute behind the new state, optional on purpose: a
 * region deployed before owner decision 12 never writes it, and an absent
 * attribute has to read as "not blocked". A dashboard that invented a block out
 * of a missing field would lock a page nobody could unlock.
 *
 * `send_block` is written by the mail stack on the first complaint that trips a
 * threshold (plan.SEND_BLOCK_ATTRIBUTE in infra), as a map:
 * `{reason, value, cmpl, sent, mnth, stmp}`, where `reason` is "complaint_count"
 * or "complaint_rate". It is written once, conditional on the attribute not
 * already existing, and ONLY staff clear it. So its `mnth` is the month it
 * tripped in, never a month to compare against now: unlike `egrblk`, this block
 * does not lapse when the month rolls over.
 *
 * The per-owner cap has no reader here, on purpose. Its counter lives in the
 * mail stack's own table, keyed by owner and month, and nothing joins it onto
 * the service row, so a dashboard cannot show a live figure for it without the
 * backend serving one first. The rule travels as copy instead
 * (FREE_OWNER_MONTHLY_SENDS), and a refused send is answered by e-mail from the
 * project's own newsletter address.
 */
export type NewsletterRowLike = {
    send_block?: unknown;
    storage_info?: { email_sends?: unknown } | null;
} | null | undefined;

/**
 * Why newsletter sending is stopped for complaints: "complaint_count" when the
 * month reached COMPLAINT_SHUTOFF_COUNT of them, "complaint_rate" when the rate
 * tripped it first. An empty string when a block is on but says nothing about
 * why, and null when nothing is blocked. Any present value counts as a block,
 * whatever its shape, because the backend writes one only to stop sending.
 */
export function complaintBlockReason(row: NewsletterRowLike): string | null {
    const flag = row?.send_block;
    if (!flag) return null;
    if (typeof flag === "string") return flag;
    if (typeof flag === "object") {
        const reason = (flag as { reason?: unknown }).reason;
        return typeof reason === "string" ? reason : "";
    }
    return "";
}

/** Whether newsletter sending is stopped for spam complaints. */
export function complaintBlocked(row: NewsletterRowLike): boolean {
    return complaintBlockReason(row) !== null;
}

export type NewsletterSendBlock =
    | { kind: "none" }
    | { kind: "complaints"; reason: string }
    | { kind: "plan_sends"; used: number; limit: number };

/**
 * Why this project cannot send a newsletter right now, in the order the backend
 * refuses one: the complaint shutoff first, because it is not a limit and no
 * upgrade lifts it, then the project's own monthly ceiling. `none` whenever
 * nothing KNOWN blocks it, which includes every per-owner refusal: see the note
 * on NewsletterRowLike.
 */
export function newsletterSendBlock(
    row: NewsletterRowLike,
    params: { monthlySendLimit?: number | null },
): NewsletterSendBlock {
    const reason = complaintBlockReason(row);
    if (reason !== null) return { kind: "complaints", reason };
    const limit = Number(params.monthlySendLimit);
    if (Number.isFinite(limit) && limit > 0) {
        const sent = Number(row?.storage_info?.email_sends);
        if (Number.isFinite(sent) && sent >= limit) return { kind: "plan_sends", used: sent, limit };
    }
    return { kind: "none" };
}
